import { naE164 } from '@insolitum/types';
import { AlertCircle, MessageSquare } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { supabase } from '../../lib/supabase';
import { useAuth } from '../../providers/AuthProvider';

// =============================================================================
// Karta „Alarmy SMS" — zgoda ZALOGOWANEJ osoby na SMS-y platformy w tej
// organizacji. Dziś SMS-em idą wyłącznie krytyczne alarmy NeuroSense
// (przekroczenie krytyczne HACCP, zalanie/dym/gaz) — mapa
// `services/neurosense-core/src/services/smsZdarzen/mapa.ts`.
//
// Numer to `users.phone` z własnego konta — ta sama kartoteka, z której czyta
// nadawca (`neurosense-core` smsService), a nie drugi rejestr ludzi. Zgoda to
// wiersz `sms_zgody` (migracja `*_sms_platformy.sql`),
// domyślnie wyłączona: bez jawnego „tak” SMS nie idzie. RLS pozwala zapisać
// wyłącznie własny wiersz w organizacji, w której ma się aktywne członkostwo.
//
// Numer sprawdzamy tą samą funkcją, którą nadawca normalizuje go przed
// Twilio (`naE164` z `@insolitum/types`), więc ekran nie przyjmie numeru,
// na który nadawca i tak by nie wysłał.
// =============================================================================

type Komunikat = { rodzaj: 'ok' | 'blad'; tekst: string } | null;

export function KartaAlarmowSms() {
  const { user, currentOrganization, impersonatingPartner } = useAuth();
  const orgId = currentOrganization?.organization_id ?? null;
  const userId = user?.id ?? null;
  const moznaZmieniac = !impersonatingPartner && !!orgId && !!userId;

  const [telefon, setTelefon] = useState('');
  const [wlaczone, setWlaczone] = useState(false);
  const [laduje, setLaduje] = useState(true);
  const [zapisuje, setZapisuje] = useState(false);
  const [komunikat, setKomunikat] = useState<Komunikat>(null);
  const klucz = useRef('');
  klucz.current = `${orgId}|${userId}`;

  const wczytaj = useCallback(async () => {
    if (!orgId || !userId) return;
    const moj = `${orgId}|${userId}`;
    setLaduje(true);
    setKomunikat(null);
    const [konto, zgoda] = await Promise.all([
      supabase.from('users').select('phone').eq('id', userId).maybeSingle(),
      supabase
        .from('sms_zgody')
        .select('wlaczone')
        .eq('organization_id', orgId)
        .eq('user_id', userId)
        .maybeSingle(),
    ]);
    if (klucz.current !== moj) return;
    if (konto.error || zgoda.error) {
      setKomunikat({
        rodzaj: 'blad',
        tekst: `Nie udało się wczytać ustawień SMS: ${(konto.error ?? zgoda.error)?.message}`,
      });
    } else {
      setTelefon(((konto.data as { phone?: string | null } | null)?.phone ?? '').trim());
      setWlaczone((zgoda.data as { wlaczone?: boolean } | null)?.wlaczone === true);
    }
    setLaduje(false);
  }, [orgId, userId]);

  useEffect(() => {
    void wczytaj();
  }, [wczytaj]);

  const zapisz = async (nowaZgoda: boolean) => {
    if (!moznaZmieniac || laduje || !orgId || !userId) return;
    const surowy = telefon.trim();
    const e164 = surowy ? naE164(surowy) : null;
    if (surowy && !e164) {
      setKomunikat({
        rodzaj: 'blad',
        tekst: 'Numer nie wygląda na telefon. Wpisz go z kierunkowym, np. +48 600 100 200.',
      });
      return;
    }
    if (nowaZgoda && !e164) {
      setKomunikat({ rodzaj: 'blad', tekst: 'Bez numeru telefonu nie ma dokąd wysłać SMS-a.' });
      return;
    }
    setZapisuje(true);
    setKomunikat(null);
    const numer = await supabase.from('users').update({ phone: e164 }).eq('id', userId);
    const zgoda = numer.error
      ? null
      : await supabase
          .from('sms_zgody')
          .upsert({ organization_id: orgId, user_id: userId, wlaczone: nowaZgoda });
    setZapisuje(false);
    const blad = numer.error ?? zgoda?.error;
    if (blad) {
      setKomunikat({ rodzaj: 'blad', tekst: `Nie zapisano: ${blad.message}` });
      return;
    }
    if (e164) setTelefon(e164);
    setWlaczone(nowaZgoda);
    setKomunikat({
      rodzaj: 'ok',
      tekst: nowaZgoda
        ? 'Zapisano. Krytyczne alarmy NeuroSense przyjdą też SMS-em, w limicie pakietu.'
        : 'Zapisano. SMS-ów nie wysyłamy — alarmy przychodzą mailem i w aplikacji.',
    });
  };

  if (!orgId || !userId) return null;

  return (
    <div className="card">
      <div className="card__head">
        <MessageSquare className="w-5 h-5 text-aurora-brand" />
        <h2 className="card__title">Alarmy SMS</h2>
      </div>
      <div className="card__body space-y-4">
        <p className="text-sm text-aurora-muted">
          Krytyczny alarm NeuroSense (temperatura poza progiem krytycznym HACCP, zalanie, dym, gaz)
          może przyjść także SMS-em na Twój telefon. To Twoja decyzja — domyślnie wyłączone. SMS-y
          liczą się do miesięcznego limitu pakietu organizacji; po jego wyczerpaniu alarmy idą dalej
          mailem i w aplikacji.
        </p>
        <div>
          <label className="field__label mb-2" htmlFor="alarmy-sms-telefon">
            Twój numer telefonu
          </label>
          <input
            id="alarmy-sms-telefon"
            type="tel"
            value={telefon}
            onChange={(e) => setTelefon(e.target.value)}
            disabled={!moznaZmieniac || laduje || zapisuje}
            placeholder="+48 600 100 200"
            className="input disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={wlaczone}
            onChange={(e) => void zapisz(e.target.checked)}
            disabled={!moznaZmieniac || laduje || zapisuje}
          />
          <span>Wysyłaj mi krytyczne alarmy SMS-em</span>
        </label>
        {wlaczone && (
          <button
            type="button"
            className="btn btn--secondary"
            onClick={() => void zapisz(true)}
            disabled={!moznaZmieniac || laduje || zapisuje}
          >
            Zapisz numer
          </button>
        )}
        {komunikat && (
          <p
            className={
              komunikat.rodzaj === 'blad'
                ? 'text-sm text-aurora-danger flex items-center gap-2'
                : 'text-sm text-aurora-success'
            }
          >
            {komunikat.rodzaj === 'blad' && <AlertCircle className="w-4 h-4" />}
            {komunikat.tekst}
          </p>
        )}
      </div>
    </div>
  );
}
